
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  DeviceSvg,
  PortGroupSvg,
  PortGroupDrawConf,
  PortDrawConf,
  PortGroupDto,
  PortDto,
  LabelDrawConf,
  SkeletonPortDrawConf,
  ShapeDrawConf,
} from 'draw-network-devices';
import {
  PortGroup,
  Product,
  PGInterface,
  InfoOfDevice,
} from '@/api/interfaces';
import { getPortType, getColorForVlanType } from './helpers';
import _get from 'lodash.get';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import { deepClone } from '@/helpers';
import { joinDeviceAndModulesInfo } from '@/components/shared/helpers';

type PortClickedEvent = Event & {
  detail: { portId: string; selected: boolean };
};

type SelectedPortsChanged = Event & {
  detail: { selectedPorts: string[] };
};

@Component
export default class DrawProduct extends Vue {
  @Prop(Object) private product!: Product;
  @Prop(Boolean) private multiple!: boolean;
  @Prop({ type: Boolean, default: false }) private hideSlider!: boolean;
  @Prop({ type: Number, default: 1.2 }) private scale!: 1.2;
  @Prop(Boolean) private disableAllPorts!: boolean;
  @Prop({ type: [], default: Array }) private modules!: {
    startingPoint: { x: number; y: number };
    position: number;
    product: Product;
  }[];

  private drawing: DeviceSvg | null = null;
  private joinedProduct: Product | null = null;
  private portGroups: { [key: string]: number } = {};
  private lastId = 1;
  private slider = 1.7;

  public redraw(): void {
    this.clearImage();
    this.initDrawing();
  }

  @Watch('product', { deep: true })
  private redrawOnDeviceInfoChange(oldVal: any, newVal: any): void {
    if (!isEqual(oldVal, newVal)) {
      this.redraw();
    }
  }

  @Watch('modules', { deep: true })
  private redrawOnModulesInfoChange(oldVal: any, newVal: any): void {
    if (!isEqual(oldVal, newVal)) {
      this.redraw();
    }
  }

  private debouncedRedraw = debounce(() => {
    this.redraw();
  }, 500);

  @Watch('slider')
  private redrawOnSliderChange(): void {
    if (this.hideSlider) {
      return;
    }
    this.debouncedRedraw();
  }

  private initDrawing(): void {
    this.joinedProduct = deepClone(this.product);
    if (this.joinedProduct != null) {
      if (this.modules.length > 0 && this.joinedProduct.info != null) {
        const modulesInfo: {
          startingPoint: { x: number; y: number };
          position: number;
          moduleInfo: InfoOfDevice;
        }[] = [];
        this.modules.forEach((m) => {
          if (m.product.info) {
            modulesInfo.push({
              position: m.position,
              startingPoint: m.startingPoint,
              moduleInfo: m.product.info,
            });
          }
        });
        // This will join the product info with the modules info and take care of deep cloning the object
        this.joinedProduct.info = joinDeviceAndModulesInfo(
          this.joinedProduct.info,
          modulesInfo,
        );
      } else {
        this.joinedProduct = this.product;
      }

      this.drawing = new DeviceSvg({
        name: _get(this.joinedProduct, 'info.name', ''),
        selector: '#drawing',
        size: _get(this.joinedProduct, 'info.size', {
          x: 800,
          y: 110,
        }),
        scale: this.hideSlider ? this.scale! : this.slider,
        radius: _get(this.joinedProduct, 'info.radius'),
        backgroundColor: _get(
          this.joinedProduct,
          'info.background_color',
          '#aaa',
        ),
        portGroups: [],
        onlyOneSelected: !this.multiple,
      });
      this.draw(this.drawing);
    }
  }

  private addPortGroup(id: number, pg: PortGroup): void {
    if (!this.drawing) {
      throw Error('No drawing created');
    }

    const portGroupDto: PortGroupDto = {
      numberOfPorts: pg.interfaces.length,
      data: {},
    };

    const portGroupDrawConf: PortGroupDrawConf = {
      rotated: pg.rotated,
      twoLines: pg.two_lines,
      mixedCount: pg.mixed_count,
      margin: pg.margin,
      startingPoint: { x: pg.starting_point.x, y: pg.starting_point.y },
      interfaces: [],
      paper: this.drawing.getPaperGroup(),
    };

    const portGroup = this.drawing.createAddPortGroup(
      id,
      portGroupDto,
      portGroupDrawConf,
    );

    // Add ports to port group
    pg.interfaces.forEach((portIface) => {
      const portData = this.getPortDtoInfo(portIface);

      if (portData) {
        const portDrawConf = this.getPortDrawInfo(
          portData.id,
          portGroup,
          portIface,
        );

        if (
          (portData.port === null || (portData.port && portData.portActive)) &&
          !portDrawConf.hidden // TODO: remove when draw-network-devices will support.
        ) {
          portGroup.addPort(portData, portDrawConf, this.multiple);
        }
      }
    });
  }

  private getPortDtoInfo(port: PGInterface): PortDto | null {
    if (port.port) {
      if (!this.portGroups[port.port]) {
        this.portGroups[port.port] = this.lastId;
        this.lastId = this.lastId + 1;
      }
    }

    const portData: PortDto = {
      id: Math.ceil(Math.random() * 100000),
      vid: 1, // TODO: use vid
      formFactor: getPortType(port.form_factor) as
        | 'RJ45'
        | 'SFP'
        | 'QSFP'
        | 'MISC',
      usage: '',
      enabled: true,
      state: 'up',
      poe: false,
      switchPortMode: 'access',
      managed: true,
      port: port.port ? this.portGroups[port.port] : null,
      portActive: _get(port, 'port_active', false),
      trustPolicy: '',
      backgroundColor: getColorForVlanType('ztp'),
    };

    return portData;
  }

  private getPortDrawInfo(
    portId: number,
    portGroup: PortGroupSvg,
    port: PGInterface,
    startingPaper = true,
  ) {
    const portDrawConf: PortDrawConf = {
      paper: startingPaper ? portGroup.getPaperGroup() : null,
      shortName: port.short_name,
      name: port.name,
      label: port.label,
      rotated: false, // is set in library
      hidden: port.hidden,
      selected: false,
      selectedColor: '#50FF00',
      position: { x: 0, y: 0 },
      disabled: this.disableAllPorts,
    } as PortDrawConf;

    return portDrawConf;
  }

  private clearImage() {
    const draw = document.querySelector('#drawing');
    const svgimg = document.querySelector('#drawing > svg');
    if (draw && svgimg) {
      draw.removeChild(svgimg);
    }
  }

  private addLabels(): void {
    const labels: LabelDrawConf[] = _get(this.joinedProduct, 'info.labels', []);
    labels.forEach((label: LabelDrawConf) => {
      if (this.drawing) {
        this.drawing.createLabel({
          paper: this.drawing.getPaperGroup(),
          name: label.name,
          hidden: false,
          color: label.color,
          fontSize: label.fontSize,
          fontWeight: label.fontWeight,
          fontFamily: label.fontFamily,
          rotation: label.rotation, // rotation in degrees
          position: label.position,
        });
      }
    });
  }

  private addSlots(): void {
    const slots: { slot: number; starting_point: { x: number; y: number } }[] =
      _get(this.joinedProduct, 'info.module_slots', []);
    if (slots) {
      slots.forEach((slot) => {
        if (this.drawing) {
          this.drawing.createShape(
            // vue has RangeError _traverse problem so object needs to be recreated here
            {
              paper: this.drawing.getPaperGroup(),
              hidden: false,
              borderWidth: 0,
              borderColor: '#000000',
              filled: true,
              fillColor: 'red',
              rotation: 0,
              position: slot.starting_point,
              properties: {
                shape: 'square',
                width: 10,
              },
            },
          );
          this.drawing.createLabel({
            paper: this.drawing.getPaperGroup(),
            name: 'Slot: ' + slot.slot.toString(),
            hidden: false,
            color: 'red',
            fontSize: 10,
            rotation: 0, // rotation in degrees
            position: {
              x: slot.starting_point.x + 12,
              y: slot.starting_point.y,
            },
          });
        }
      });
    }
  }

  private addSkeletonPorts(): void {
    const sPorts: SkeletonPortDrawConf[] = _get(
      this.joinedProduct,
      'info.skeletonPorts',
      [],
    );
    sPorts.forEach((port: SkeletonPortDrawConf) => {
      if (this.drawing) {
        this.drawing.createSkeletonPort({
          paper: this.drawing.getPaperGroup(),
          hidden: port.hidden,
          color: port.color,
          formFactor: port.formFactor,
          rotation: port.rotation, // rotation in degrees
          position: port.position,
        });
      }
    });
  }

  private addShapes(): void {
    const shapes: ShapeDrawConf[] = _get(this.joinedProduct, 'info.shapes', []);
    shapes.forEach((shape: ShapeDrawConf) => {
      if (this.drawing) {
        this.drawing.createShape(
          // vue has RangeError _traverse problem so object needs to be recreated here
          {
            paper: this.drawing.getPaperGroup(),
            hidden: shape.hidden,
            borderWidth: shape.borderWidth,
            borderColor: shape.borderColor,
            filled: shape.filled,
            fillColor: shape.fillColor,
            rotation: shape.rotation,
            position: shape.position,
            properties: shape.properties,
          },
        );
      }
    });
  }

  private draw(parent: DeviceSvg): void {
    if (!this.drawing) {
      throw Error('Trying to draw before initial');
    }
    this.addShapes();
    this.addSkeletonPorts();
    this.addLabels();
    this.addSlots();
    const portGroups: PortGroup[] = _get(
      this.joinedProduct,
      'info.port_groups',
      [],
    );
    portGroups.forEach((pg: PortGroup, index) => {
      this.addPortGroup(index, pg);
    });
    parent.draw();
  }

  private created() {
    if (this.scale !== null) {
      this.slider = this.scale;
    }
    this.joinedProduct = deepClone(this.product);
  }

  private mounted() {
    this.initDrawing();
  }
}
